import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, X, History } from "lucide-react";
import { resolveMedia } from "@/lib/api";
import { continueList, clearProgress, watchPath } from "@/lib/progress";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

export const ContinueWatching = () => {
  const [items, setItems] = useState([]);
  useEffect(() => setItems(continueList()), []);
  if (!items.length) return null;

  const remove = (p) => {
    clearProgress(p.animeId, p.number, p.season || 1);
    setItems(continueList());
  };

  return (
    <section data-testid="continue-watching-section" className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-24">
      <div className="flex items-center gap-3 mb-8">
        <History size={16} className="text-[#00F0FF]" />
        <span className="kp-eyebrow">Continue watching</span>
      </div>
      <div className="flex gap-5 overflow-x-auto pb-4 -mx-5 px-5 sm:-mx-8 sm:px-8 snap-x">
        {items.map((p, i) => (
          <motion.div
            key={`${p.animeId}:${p.season || 1}:${p.number}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07, duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="group relative shrink-0 w-72 sm:w-80 snap-start"
            data-testid={`continue-card-${p.animeId}-${p.number}`}
          >
            <Link to={watchPath(p.animeId, p.number, p.season || 1)} className="block relative aspect-video overflow-hidden kp-card-clip border border-white/[0.08] group-hover:border-[#00F0FF]/50 transition-colors">
              <img src={resolveMedia(p.cover)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08070C] via-[#08070C]/40 to-transparent" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="w-12 h-12 rounded-full bg-[#00F0FF] text-[#08070C] flex items-center justify-center kp-glow-cyan">
                  <Play size={16} className="fill-current ml-0.5" />
                </span>
              </div>
              <div className="absolute bottom-0 inset-x-0 p-4">
                <div className="kp-eyebrow !text-[0.55rem] mb-1">{(p.season || 1) > 1 ? `S${p.season} · ` : ""}EP {String(p.number).padStart(2, "0")} · {fmt(p.time)} / {fmt(p.duration)}</div>
                <div className="font-display text-sm font-semibold truncate">{p.animeTitle}</div>
                <div className="text-xs text-[#94A3B8] truncate">{p.epTitle}</div>
              </div>
              <div className="absolute bottom-0 inset-x-0 h-[3px] bg-white/10">
                <div className="h-full bg-[#00F0FF]" style={{ width: `${Math.round((p.time / p.duration) * 100)}%` }} />
              </div>
            </Link>
            <button data-testid={`continue-remove-${p.animeId}-${p.number}`} onClick={() => remove(p)} className="absolute top-2 right-2 w-7 h-7 kp-glass flex items-center justify-center text-[#94A3B8] hover:text-white opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity" aria-label="Remove">
              <X size={13} />
            </button>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
